import {
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  getDay,
  isValid,
} from 'date-fns';

/** Order matches the day-selector row; `dayIndex` is date-fns' getDay value. */
export const WEEKDAYS = [
  {
    key: 'monday', short: 'M', abbr: 'Mon', label: 'Monday', dayIndex: 1,
  },
  {
    key: 'tuesday', short: 'T', abbr: 'Tue', label: 'Tuesday', dayIndex: 2,
  },
  {
    key: 'wednesday', short: 'W', abbr: 'Wed', label: 'Wednesday', dayIndex: 3,
  },
  {
    key: 'thursday', short: 'Th', abbr: 'Thu', label: 'Thursday', dayIndex: 4,
  },
  {
    key: 'friday', short: 'F', abbr: 'Fri', label: 'Friday', dayIndex: 5,
  },
  {
    key: 'saturday', short: 'Sa', abbr: 'Sat', label: 'Saturday', dayIndex: 6,
  },
  {
    key: 'sunday', short: 'Su', abbr: 'Sun', label: 'Sunday', dayIndex: 0,
  },
];

const isUsableDate = (date) => !!date && isValid(date);

/**
 * Counts calendar days from start to end, inclusive.
 * @param {Date|null} start
 * @param {Date|null} end
 * @returns {number} 0 when either date is missing or end precedes start
 */
export const countDaysInclusive = (start, end) => {
  if (!isUsableDate(start) || !isUsableDate(end)) {
    return 0;
  }
  return Math.max(differenceInCalendarDays(end, start) + 1, 0);
};

/**
 * Counts the days in a range that fall on one of the selected weekdays.
 * @param {Date|null} start
 * @param {Date|null} end
 * @param {Object<string, boolean>} selectedDays keyed by WEEKDAYS[].key
 * @returns {number}
 */
export const countSelectedWeekdaysInRange = (start, end, selectedDays) => {
  if (countDaysInclusive(start, end) === 0) {
    return 0;
  }
  const selectedIndexes = WEEKDAYS
    .filter((day) => selectedDays[day.key])
    .map((day) => day.dayIndex);
  return eachDayOfInterval({ start, end })
    .filter((date) => selectedIndexes.includes(getDay(date)))
    .length;
};

/**
 * @param {Object<string, boolean>} selectedDays keyed by WEEKDAYS[].key
 * @returns {string} e.g. "Mon, Wed, Fri"
 */
export const formatSelectedWeekdays = (selectedDays) => WEEKDAYS
  .filter((day) => selectedDays[day.key])
  .map((day) => day.abbr)
  .join(', ');

/**
 * @param {Date|null} date
 * @returns {string} e.g. "Mon, Sep 14", or '' for a missing date
 */
export const formatShortDate = (date) => (isUsableDate(date) ? format(date, 'EEE, MMM d') : '');

/**
 * @param {number} count
 * @param {string} noun singular form
 * @returns {string} e.g. "1 day", "12 days"
 */
export const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;
